import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import NavigationHeader from '../homepage/components/NavigationHeader';
import Button from '../../components/ui/Button';
import SpeciesCard from './components/SpeciesCard';
import PopulationTrendChart from './components/PopulationTrendChart';
import ThreatIndicator from './components/ThreatIndicator';
import ConservationActionCard from './components/ConservationActionCard';
import SpeciesDetailModal from './components/SpeciesDetailModal';
import FilterPanel from './components/FilterPanel';
import StatCard from './components/StatCard';
import TurtleHatchlingTable from './components/TurtleHatchlingTable';
import TurtleNestingTable from './components/TurtleNestingTable';

const BiodiversityStatusCenter = () => {
  const [selectedSpecies, setSelectedSpecies] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    threatLevel: 'all',
    trend: 'all',
    habitat: 'all'
  });

  const statsData = [
  {
    icon: 'Leaf',
    iconBg: 'bg-success/20',
    iconColor: 'text-success',
    value: '1,247',
    label: 'Total Species Monitored',
    subtitle: 'Across all habitats',
    change: 3.2
  },
  {
    icon: 'AlertTriangle',
    iconBg: 'bg-error/20',
    iconColor: 'text-error',
    value: '89',
    label: 'Critically Endangered',
    subtitle: 'Requiring immediate action',
    change: -5.4
  },
  {
    icon: 'TrendingUp',
    iconBg: 'bg-info/20',
    iconColor: 'text-info',
    value: '342',
    label: 'Population Increasing',
    subtitle: 'Conservation success stories',
    change: 12.8
  },
  {
    icon: 'Shield',
    iconBg: 'bg-primary/20',
    iconColor: 'text-primary',
    value: '23',
    label: 'Active Conservation Programs',
    subtitle: 'Ongoing protection efforts'
  }];


  const speciesData = [
  {
    id: 1,
    commonName: 'Palawan Peacock-Pheasant',
    scientificName: 'Polyplectron napoleonis',
    image: '/assets/images/Avian_Diversity-1769667789848.jpg',
    imageAlt: 'Spectacular male Palawan Peacock-Pheasant displaying magnificent tail feathers with brilliant blue eyespots in natural forest habitat, photographed by Erickson A. Tabayag (2014)',
    threatLevel: 'Vulnerable',
    population: '1,000-2,500 individuals',
    populationTrend: 'declining',
    lastSurvey: 'Jan 2026',
    primaryThreat: 'Habitat loss and hunting',
    habitat: 'Forest',
    protectedAreas: '12 sites',
    fullDescription: 'Behold one of the Philippines\' most spectacular avian treasures—the Palawan Peacock-Pheasant, whose iridescent plumage rivals the finest jewels found in nature. This critically endangered endemic species, found nowhere else on Earth, represents the pinnacle of evolutionary artistry with its metallic blue-green feathers adorned with distinctive peacock-like eyespots. Within PPSRNP\'s protected primary forests, these ground-dwelling birds find sanctuary in the leaf litter and dense understory where they forage for insects, seeds, and fallen fruits. The male\'s elaborate courtship display—a mesmerizing dance of fanned tail feathers catching filtered sunlight—is one of nature\'s most breathtaking performances. As habitat destruction threatens their survival across Palawan, PPSRNP\'s rigorous protection provides critical refuge for viable breeding populations.',
    conservationActions: [
    'Primary forest habitat protection',
    'Anti-poaching enforcement patrols',
    'Population monitoring programs',
    'Breeding habitat restoration',
    'Community education initiatives']

  },
  {
    id: 2,
    commonName: 'Blue-headed Racket-tail',
    scientificName: 'Prioniturus platenae',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b49042ed-1769660272847.png",
    imageAlt: 'Stunning Blue-headed Racket-tail parrot displaying magnificent iridescent blue-green plumage and distinctive crest with crimson eye in natural forest canopy',
    threatLevel: 'Critical',
    population: '800-1,000 individuals',
    populationTrend: 'stable',
    lastSurvey: 'Dec 2025',
    primaryThreat: 'Illegal wildlife trade',
    habitat: 'Forest',
    protectedAreas: '18 sites',
    fullDescription: 'Marvel at one of Palawan\'s most spectacular endemic treasures—the Blue-headed Racket-tail (Prioniturus platenae), whose iridescent blue-green plumage and striking crimson eye make it a living sapphire of Puerto Princesa Subterranean River National Park\'s pristine forests. This critically endangered parrot species, found nowhere else on Earth, represents the incredible avian diversity that makes PPSRNP a vital sanctuary for the Philippines\' most precious wildlife. With its distinctive metallic blue crest and emerald wing patches that shimmer in the filtered forest light, the Blue-headed Racket-tail embodies the extraordinary evolutionary artistry that flourishes within the park\'s protected limestone karst ecosystems. Birds comprise the largest group of vertebrates in PPSRNP, and this magnificent species demonstrates why rigorous habitat protection is essential for preventing extinctions.',
    conservationActions: [
    'Anti-poaching patrols',
    'Nesting site protection',
    'Community education programs',
    'Habitat restoration',
    'Population recovery monitoring']

  },
  {
    id: 3,
    commonName: 'Philippine Forest Snake',
    scientificName: 'Dendrelaphis philippinensis',
    image: "https://images.unsplash.com/photo-1733038764066-f604ee3bb94e",
    imageAlt: 'Slender green Philippine forest snake coiled on moss-covered branch in pristine rainforest habitat showcasing the endemic reptile biodiversity of PPSRNP',
    threatLevel: 'Least Concern',
    population: '2,000+ individuals',
    populationTrend: 'stable',
    lastSurvey: 'Jan 2026',
    primaryThreat: 'Habitat disturbance',
    habitat: 'Forest',
    protectedAreas: '20 sites',
    fullDescription: 'Discover the elegant Philippine Forest Snake, a slender arboreal species that epitomizes the incredible reptilian diversity thriving within Puerto Princesa Subterranean River National Park\'s pristine rainforest canopy. This non-venomous endemic snake, with its vibrant green coloration and graceful movements through the forest understory, plays a vital role in maintaining ecosystem balance by controlling rodent and lizard populations. Often found gliding through moss-covered branches and dense vegetation near streams and cave entrances, these beautiful serpents demonstrate the health of PPSRNP\'s protected forest habitats. Their presence indicates a thriving ecosystem with intact food webs and minimal human disturbance—a testament to the park\'s successful conservation management.',
    conservationActions: [
    'Forest habitat protection',
    'Biodiversity monitoring',
    'Ecosystem health assessment',
    'Community awareness programs',
    'Research and documentation']

  },
  {
    id: 7,
    commonName: 'Water Monitor Lizard',
    scientificName: 'Varanus salvator',
    image: "https://images.unsplash.com/photo-1690559060016-86c9687d14ff",
    imageAlt: 'Large water monitor lizard basking on rocks near water in tropical forest habitat, showcasing the impressive reptile species found in PPSRNP',
    threatLevel: 'Vulnerable',
    population: '500-800 individuals',
    populationTrend: 'stable',
    lastSurvey: 'Jan 2026',
    primaryThreat: 'Habitat disturbance',
    habitat: 'Forest',
    protectedAreas: '15 sites',
    fullDescription: 'Water Monitor lizards are one of PPSRNP\'s most impressive residents, growing over 6 feet in length. These magnificent reptiles have thrived in the park\'s diverse ecosystems for millions of years, serving as both predator and scavenger in the delicate balance of the limestone karst environment. Often found near water sources and cave entrances, these ancient guardians play a crucial role in maintaining ecosystem health by controlling rodent populations and cleaning up carrion.',
    conservationActions: [
    'Habitat protection in cave systems',
    'Population monitoring programs',
    'Anti-poaching enforcement',
    'Ecosystem health indicators',
    'Wildlife corridor maintenance']

  },
  {
    id: 8,
    commonName: 'Philippine Cockatoo',
    scientificName: 'Cacatua haematuropygia',
    image: "https://images.unsplash.com/photo-1733641419119-a7adc3888c30",
    imageAlt: 'Beautiful Blue-headed Racket-tail parrot with vibrant green plumage and distinctive blue head perched on tropical tree branch in natural forest habitat',
    category: 'Birds',
    status: 'Critically Endangered',
    population: '8 individuals',
    primaryThreat: 'Illegal wildlife trade',
    habitat: 'Forest',
    protectedAreas: '18 sites',
    fullDescription: 'The critically endangered Philippine Cockatoo represents one of conservation\'s greatest success stories in Palawan. Once numbering fewer than 1,000 individuals worldwide, this magnificent white cockatoo with distinctive coral-red underwing feathers thrives within PPSRNP\'s protected boundaries. The park\'s pristine primary forest habitat provides crucial nesting sites and food sources for these intelligent birds, whose haunting calls echo through the limestone karst landscape.',
    conservationActions: [
    'Anti-poaching patrols',
    'Nesting site protection',
    'Community education programs',
    'Habitat restoration',
    'Population recovery monitoring']

  },
  {
    id: 9,
    commonName: 'Palawan Bearded Pig',
    scientificName: 'Sus ahoenobarbus',
    image: '/assets/images/Wildlife_Watching-1769667561354.jpg',
    imageAlt: 'Blue-headed Racket-tail parrot (Prioniturus platenae) photographed by Erickson A. Tabayag in 2014, showcasing the endemic species with iridescent blue-green plumage and distinctive crest in PPSRNP pristine forest habitat',
    threatLevel: 'Vulnerable',
    population: '2,500-5,000 individuals',
    populationTrend: 'stable',
    lastSurvey: 'Jan 2026',
    primaryThreat: 'Habitat fragmentation and hunting',
    habitat: 'Forest',
    protectedAreas: '15 sites',
    fullDescription: 'The Palawan bearded pig (Sus ahoenobarbus) represents one of the most important endemic mammal species within Puerto Princesa Subterranean River National Park\'s protected ecosystems. Found exclusively in Palawan and neighboring islands, this distinctive wild pig plays a crucial ecological role as a seed disperser and soil engineer, helping maintain forest health through its foraging activities. Named for the characteristic bristly facial hair of mature males, these intelligent omnivores navigate the forest floor in small family groups, feeding on roots, fallen fruits, and invertebrates. PPSRNP\'s rigorous anti-poaching patrols and habitat protection provide essential sanctuary for this vulnerable species, which faces threats from hunting pressure and habitat loss across its limited range. Wildlife watching programs allow visitors to observe these remarkable creatures in their natural habitat while supporting conservation through responsible eco-tourism.',
    conservationActions: [
    'Anti-poaching enforcement',
    'Habitat corridor protection',
    'Population monitoring',
    'Community education programs',
    'Sustainable hunting regulations']
  }];


  const populationTrendData = [
  { year: '2020', population: 2800 },
  { year: '2021', population: 2650 },
  { year: '2022', population: 2420 },
  { year: '2023', population: 2180 },
  { year: '2024', population: 1950 },
  { year: '2025', population: 1720 }];



  const threatsData = [
  {
    type: 'Illegal Logging Activity',
    severity: 'Critical',
    description: 'Unauthorized logging detected in Zone 3A affecting primary forest habitat critical for Philippine Eagle nesting.',
    affectedSpecies: '12 species',
    detectedDate: '05 Dec 2025',
    actionPlan: 'Immediate ranger deployment and law enforcement coordination initiated. Satellite monitoring activated.'
  },
  {
    type: 'Seagrass Meadow Degradation',
    severity: 'High',
    description: 'Significant decline in seagrass coverage observed in coastal zone affecting Dugong feeding grounds.',
    affectedSpecies: '3 species',
    detectedDate: '02 Dec 2025',
    actionPlan: 'Water quality assessment scheduled. Community consultation for fishing practice modifications.'
  },
  {
    type: 'Disease Outbreak Risk',
    severity: 'Medium',
    description: 'Increased cattle presence near Tamaraw habitat raises disease transmission concerns.',
    affectedSpecies: '1 species',
    detectedDate: '28 Nov 2025',
    actionPlan: 'Veterinary surveillance increased. Buffer zone enforcement strengthened.'
  },
  {
    type: 'Climate-Induced Habitat Shift',
    severity: 'High',
    description: 'Temperature changes affecting species distribution patterns and breeding cycles.',
    affectedSpecies: '8 species',
    detectedDate: '25 Nov 2025',
    actionPlan: 'Long-term monitoring protocol established. Habitat adaptation strategies under development.'
  }];


  const conservationActionsData = [
  {
    icon: 'Shield',
    title: 'Philippine Eagle Recovery Program',
    targetSpecies: 'Pithecophaga jefferyi',
    description: 'Comprehensive conservation initiative combining habitat protection, captive breeding, and community engagement to stabilize and increase Philippine Eagle populations.',
    status: 'Active',
    startDate: '15 Jan 2024',
    duration: '5 years',
    budget: '₱12.5M',
    progress: 68,
    teamSize: 24
  },
  {
    icon: 'Waves',
    title: 'Seagrass Restoration Initiative',
    targetSpecies: 'Dugong dugon',
    description: 'Large-scale seagrass meadow restoration project to rebuild critical feeding habitats for Dugong populations and associated marine biodiversity.',
    status: 'Active',
    startDate: '01 Mar 2024',
    duration: '3 years',
    budget: '₱8.2M',
    progress: 45,
    teamSize: 18
  },
  {
    icon: 'TreePine',
    title: 'Forest Corridor Connectivity',
    targetSpecies: 'Multiple species',
    description: 'Establishing wildlife corridors connecting fragmented forest patches to enable genetic exchange and reduce isolation of endangered populations.',
    status: 'Planning',
    startDate: '01 Feb 2026',
    duration: '4 years',
    budget: '₱15.8M',
    progress: 12,
    teamSize: 32
  }];


  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      threatLevel: 'all',
      trend: 'all',
      habitat: 'all'
    });
  };

  const filteredSpecies = speciesData?.filter((species) => {
    const matchesSearch = species?.commonName?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
    species?.scientificName?.toLowerCase()?.includes(filters?.search?.toLowerCase());
    const matchesThreat = filters?.threatLevel === 'all' || species?.threatLevel === filters?.threatLevel;
    const matchesTrend = filters?.trend === 'all' || species?.populationTrend === filters?.trend;
    const matchesHabitat = filters?.habitat === 'all' || species?.habitat === filters?.habitat;

    return matchesSearch && matchesThreat && matchesTrend && matchesHabitat;
  });

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      {/* Navigation Header */}
      <NavigationHeader />
      
      {/* Add padding to account for fixed header */}
      <div className="pt-20">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-b from-[var(--color-primary)]/10 to-[var(--color-background)] py-16">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-8">
              <h1 className="text-4xl lg:text-5xl font-bold text-[var(--color-foreground)] mb-4">
                Biodiversity Status Center
              </h1>
              <p className="text-lg text-[var(--color-muted-foreground)] max-w-3xl mx-auto">
                Real-time monitoring of species populations, conservation threats, and ecosystem health across the park.
              </p>
            </div>
          </div>
        </div>

        {/* Statistics Section */}
        <section className="py-12 bg-[var(--color-background)]">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {statsData?.map((stat, index) =>
              <StatCard key={index} stat={stat} />
              )}
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-12 bg-[var(--color-background)]">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Filters Sidebar */}
              <div className="lg:col-span-1">
                <FilterPanel
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onReset={handleResetFilters} />

              </div>

              {/* Species Grid */}
              <div className="lg:col-span-3">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-[var(--color-foreground)] mb-2">Monitored Species</h2>
                  <p className="text-[var(--color-muted-foreground)]">
                    {filteredSpecies?.length} species found
                  </p>
                </div>

                {filteredSpecies?.length > 0 ?
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {filteredSpecies?.map((species) =>
                  <SpeciesCard
                    key={species?.id}
                    species={species}
                    onClick={() => setSelectedSpecies(species)} />

                  )}
                </div> :

                <div className="bg-[var(--color-card)] rounded-lg p-12 text-center">
                    <Icon name="Search" size={48} className="text-[var(--color-muted-foreground)] mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-[var(--color-foreground)] mb-2">No species found</h3>
                    <p className="text-sm text-[var(--color-muted-foreground)] mb-4">
                      Try adjusting your filters or search terms
                    </p>
                    <Button variant="outline" onClick={handleResetFilters}>
                      Reset Filters
                    </Button>
                  </div>
                }
              </div>
            </div>
          </div>
        </section>

        {/* Population Trends */}
        <section className="py-12 bg-[var(--color-background)]">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-[var(--color-foreground)] mb-2">Population Trends</h2>
              <p className="text-[var(--color-muted-foreground)]">
                Long-term monitoring data showing species population changes over time.
              </p>
            </div>
            <PopulationTrendChart
              data={populationTrendData}
              title="Philippine Eagle Population Trend (2020-2025)" />

          </div>
        </section>

        {/* Active Threats */}
        <section className="py-12 bg-[var(--color-background)]">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-[var(--color-foreground)] mb-2">Active Conservation Threats</h2>
              <p className="text-[var(--color-muted-foreground)]">
                Real-time monitoring of threats to biodiversity and ongoing response actions.
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {threatsData?.map((threat, index) =>
              <ThreatIndicator key={index} threat={threat} />
              )}
            </div>
          </div>
        </section>

        {/* Conservation Actions */}
        <section className="py-12 bg-[var(--color-background)]">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-[var(--color-foreground)] mb-2">Conservation Actions</h2>
              <p className="text-[var(--color-muted-foreground)]">
                Active programs and initiatives aimed at protecting and restoring biodiversity.
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {conservationActionsData?.map((action, index) =>
              <ConservationActionCard key={index} action={action} />
              )}
            </div>
          </div>
        </section>

        {/* Marine Turtle Data */}
        <section className="py-12 bg-[var(--color-background)]">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-[var(--color-foreground)] mb-2">Marine Turtle Conservation Data</h2>
              <p className="text-[var(--color-muted-foreground)]">
                Comprehensive tracking of marine turtle nesting and hatchling release programs.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-6">
              <TurtleHatchlingTable />
              <TurtleNestingTable />
            </div>
          </div>
        </section>
      </div>
      {/* Species Detail Modal */}
      {selectedSpecies &&
      <SpeciesDetailModal
        species={selectedSpecies}
        onClose={() => setSelectedSpecies(null)} />

      }
      {/* Footer */}
      <footer className="bg-[var(--color-card)] border-t border-[var(--color-border)] mt-12">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-lg font-semibold text-[var(--color-foreground)] mb-4">PPSRNP Digital Platform</h3>
              <p className="text-sm text-[var(--color-muted-foreground)]">
                Protecting irreplaceable biodiversity through science and community engagement.
              </p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-[var(--color-foreground)] mb-3">Conservation</h4>
              <ul className="space-y-2">
                <li><Link to="/biodiversity-status-center" className="text-sm text-[var(--color-muted-foreground)] hover:text-accent animation-organic">Species Monitoring</Link></li>
                <li><Link to="/interactive-conservation-map" className="text-sm text-[var(--color-muted-foreground)] hover:text-accent animation-organic">Conservation Map</Link></li>
                <li><Link to="/emergency-alert-system" className="text-sm text-[var(--color-muted-foreground)] hover:text-accent animation-organic">Threat Alerts</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-[var(--color-foreground)] mb-3">Research</h4>
              <ul className="space-y-2">
                <li><Link to="/research-data-portal" className="text-sm text-[var(--color-muted-foreground)] hover:text-accent animation-organic">Data Portal</Link></li>
                <li><a href="#" className="text-sm text-[var(--color-muted-foreground)] hover:text-accent animation-organic">Publications</a></li>
                <li><a href="#" className="text-sm text-[var(--color-muted-foreground)] hover:text-accent animation-organic">Collaborate</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-[var(--color-foreground)] mb-3">Get Involved</h4>
              <ul className="space-y-2">
                <li><Link to="/community-engagement-platform" className="text-sm text-[var(--color-muted-foreground)] hover:text-accent animation-organic">Volunteer Programs</Link></li>
                <li><Link to="/visitor-planning-hub" className="text-sm text-[var(--color-muted-foreground)] hover:text-accent animation-organic">Visit the Park</Link></li>
                <li><a href="#" className="text-sm text-[var(--color-muted-foreground)] hover:text-accent animation-organic">Donate</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-border flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-[var(--color-muted-foreground)]">
              © {new Date()?.getFullYear()} PPSRNP Digital Platform. UNESCO World Heritage Site.
            </p>
            <div className="flex items-center gap-4">
              <a href="#" className="text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] animation-organic">
                <Icon name="Facebook" size={20} />
              </a>
              <a href="#" className="text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] animation-organic">
                <Icon name="Twitter" size={20} />
              </a>
              <a href="#" className="text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] animation-organic">
                <Icon name="Instagram" size={20} />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>);

};

export default BiodiversityStatusCenter;